import { Link } from '@tanstack/react-router'
import { orderBy, query, where } from 'firebase/firestore'
import {
  Activity,
  Clock,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { collectionRef } from '@/lib/firebase/firestore'
import { listenToCollection } from '@/lib/firebase/utils'

type CheckinRecord = {
  id: string
  fullName: string
  displayName: string
  pictureUrl?: string
  followers: number
  createdAt: string
}

export const DashboardPage = () => {
  const [checkins, setCheckins] = useState<CheckinRecord[]>([])

  // Real-time listener for check-ins
  useEffect(() => {
    const unsubscribe = listenToCollection(
      query(
        collectionRef('guests'),
        where('status', '==', 'checked-in'),
        orderBy('checkedInAt', 'desc'),
      ),
      setCheckins,
      {
        map: (doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            fullName: data.fullName || '',
            displayName: data.displayName || data.fullName || '',
            pictureUrl: data.lineUser?.pictureUrl,
            followers: data.expectedFollowers || 1,
            createdAt: data.checkedInAt?.toDate
              ? data.checkedInAt.toDate().toISOString()
              : data.checkedInAt || new Date().toISOString(),
          }
        },
      },
    )

    return () => {
      unsubscribe()
    }
  }, [])

  // Calculate Stats
  const totalCheckedInGroups = checkins.length
  const totalArrivedGuests = checkins.reduce(
    (acc, c) => acc + (c.followers || 1),
    0,
  )
  const followersArrived = totalArrivedGuests - totalCheckedInGroups

  const recentCheckins = checkins.slice(0, 5)

  return (
    <div className="flex-1 overflow-auto p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            แดชบอร์ด
          </h1>
          <p className="text-gray-500">
            ข้อมูลวิเคราะห์และติดตามสถานะผู้ร่วมงานแบบเรียลไทม์
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-none shadow-sm transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                กลุ่มที่เช็กอินแล้ว
              </CardTitle>
              <Users className="size-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalCheckedInGroups}</div>
              <p className="text-xs text-gray-500">จำนวนกลุ่มที่เช็กอินแล้ว</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                แขกที่มาแล้ว (รวม)
              </CardTitle>
              <UserPlus className="size-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalArrivedGuests}</div>
              <p className="text-xs text-gray-500">รวมผู้ติดตามทั้งหมด</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                แขกหลัก
              </CardTitle>
              <UserCheck className="size-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {totalCheckedInGroups}
              </div>
              <p className="text-xs text-gray-500">จำนวนแขกหลักที่เช็กอิน</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm transition-all hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                ผู้ติดตาม
              </CardTitle>
              <TrendingUp className="size-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{followersArrived}</div>
              <p className="text-xs text-gray-500">จำนวนผู้ติดตามที่มาด้วย</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-8 lg:grid-cols-7">
          {/* Main Chart/Analytics Area */}
          <Card className="border-none shadow-sm lg:col-span-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>ภาพรวมการมาร่วมงาน</CardTitle>
                  <p className="text-sm text-gray-500">
                    ความคืบหน้าการเช็กอินแบบเรียลไทม์
                  </p>
                </div>
                <Activity className="size-5 animate-pulse text-green-500" />
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="flex flex-col items-center justify-center gap-y-4 pt-4">
                <div className="relative flex size-48 items-center justify-center">
                  <svg className="size-full -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="transparent"
                      className="text-gray-100"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="transparent"
                      strokeDasharray={2 * Math.PI * 88}
                      strokeDashoffset={0}
                      strokeLinecap="round"
                      className="text-emerald-500 transition-all duration-1000 ease-in-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-gray-900">
                      {totalArrivedGuests}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <div className="size-1.5 animate-pulse rounded-full bg-emerald-500" />
                      <span className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">
                        มาแล้ว (Live)
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex w-full max-w-xs flex-col gap-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full bg-emerald-500" />
                      <span className="text-gray-600">มาถึงแล้ว</span>
                    </div>
                    <span className="font-bold">{totalArrivedGuests}</span>
                  </div>
                  <div className="h-px bg-gray-100" />
                  <div className="grid grid-cols-2 gap-4 text-center text-[10px] tracking-tighter text-gray-500 uppercase">
                    <div className="space-y-1">
                      <div className="text-sm font-bold text-gray-900">
                        {totalCheckedInGroups}
                      </div>
                      <div>แขกหลัก</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-bold text-gray-900">
                        {followersArrived}
                      </div>
                      <div>ผู้ติดตาม</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="text-muted-foreground size-4" />
                <CardTitle className="text-base font-bold">
                  เช็กอินล่าสุด
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-black/[0.05]">
                {recentCheckins.length > 0 ? (
                  recentCheckins.map((checkin) => (
                    <div
                      key={checkin.id}
                      className="flex items-center gap-4 p-4 transition-colors hover:bg-black/[0.01]"
                    >
                      <Avatar className="size-10 border-2 border-white shadow-sm">
                        <AvatarImage src={checkin.pictureUrl} />
                        <AvatarFallback className="bg-muted text-xs font-bold uppercase">
                          {checkin.fullName.slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground truncate text-sm font-bold">
                          {checkin.fullName}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {checkin.followers > 1
                            ? `+${checkin.followers - 1} ผู้ติดตาม`
                            : 'เพิ่งเช็กอิน'}
                        </p>
                      </div>
                      <div className="text-muted-foreground text-[10px] font-medium">
                        {new Date(checkin.createdAt)
                          .toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })
                          .toUpperCase()}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center p-12 text-center">
                    <UserCheck className="text-muted mb-2 size-8" />
                    <p className="text-muted-foreground text-sm">
                      ยังไม่มีการเช็กอิน
                    </p>
                  </div>
                )}
              </div>
              {checkins.length > 5 && (
                <div className="border-t border-black/[0.05] bg-black/[0.02] p-3 text-center">
                  <Link
                    to="/admin/guests"
                    search={{ tab: 'checkin' }}
                    className="text-sm font-bold text-[#0066ff] hover:underline"
                  >
                    ดูการเช็กอินทั้งหมด {checkins.length} รายการ
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
